import gql from "graphql-tag"

import {
  RESET_BOOKING_STATE,
  RESET_AVAILS,
  UPDATE_PACKAGE_CHOICE,
  RESET_PACKAGE_CHOICE,
  UPDATE_STUDENT,
  UPDATE_ADMIN_STUDENT,
  UPDATE_PACKAGE_SELECT_SKIPPED,
  SET_PRICE_BREAKDOWN,
} from "./types"
import { uuidv4 } from "../utils/misc"
import {
  InstructorLesson,
  LessonDetails,
  LessonStudentDetails,
  PriceBreakdown,
  Product,
  ProductPackageWithPrices,
  TrackingOrderInfo,
  User,
  UserLesson,
} from "../types"
import { AppThunkAction } from "../store/configureStore"
import { formatDate } from "../utils/dateUtils"
import clientNext from "../ApolloClientNext"
import {
  GQLPaymentCategories,
  GQLUsStateId,
  GQLExpireProductsTypes,
  GQLItemToRefundInput,
  GQLLessonUpdateInput,
  GQLProductQuantity,
  GQLItemToTransferInput,
  GQLItemToExpireInput,
  GQLLessonUpdateQuery,
  GQLInstructorLessonUpdateInput,
} from "../graphqlTypes"
import { trackOrderComplete } from "../utils/orderUtils"
import {
  DRIVING_MINUTE_PRODUCT_ID,
  OBSERVATION_MINUTE_PRODUCT_ID,
} from "../components/admin/common/constants"
import {
  CancelLessonDocument,
  CreatePurchaseDocument,
  EditPurchaseDocument,
  ExpireProductsDocument,
  TransferExpirationDocument,
  InstructorUpdateLessonDocument,
  RefundPurchaseDocument,
  RemoveStudentFromLessonDocument,
  ScheduleLessonDocument,
  TransferLessonDocument,
  TransferPurchaseDocument,
  UpdateLessonDocument,
  VoidExpirationDocument,
  VoidPurchaseDocument,
  SelfBookSharedLessonDocument,
  UpdateCourseOrderShippingDocument,
} from "./bookingActions.graphqlTypes"
import { VoidRefundDocument } from "../components/admin/common/PurchaseLogTable.graphqlTypes"
import { CancelPolicy } from "../utils/lessonCancel"
import { toGQLInitiatedBy } from "../config"

gql`
  mutation expireProducts($input: ExpireProductsInput!) {
    expireProducts(input: $input) {
      id
    }
  }
`

gql`
  mutation VoidExpiration($input: VoidExpirationInput!) {
    purchaseLog: voidExpiration(input: $input) {
      id
    }
  }
`

gql`
  mutation TransferExpiration($input: TransferExpirationInput!) {
    transferExpiration(input: $input) {
      id
    }
  }
`

gql`
  mutation TransferPurchase($input: TransferPurchaseInput!) {
    transferPurchase(input: $input) {
      id
    }
  }
`

gql`
  mutation TransferLesson($input: TransferLessonInput!) {
    transferLesson(input: $input) {
      id
    }
  }
`

gql`
  fragment LessonForBooking on Lesson {
    id
    bookedAt
    startTime
    endTime
    actualDurationMinutes
    instructor {
      id
      name
      timeZone
    }
  }

  mutation ScheduleLesson($input: ScheduleLessonInput!) {
    scheduleLesson(input: $input) {
      ...LessonForBooking
    }
  }

  mutation SelfBookSharedLesson($input: SelfBookSharedLessonInput!) {
    selfBookSharedLesson(input: $input) {
      ...LessonForBooking
    }
  }
`

gql`
  mutation UpdateLesson(
    $query: LessonUpdateQuery!
    $update: LessonUpdateInput!
  ) {
    updateLesson(query: $query, update: $update) {
      id
    }
  }
`

gql`
  mutation CancelLesson(
    $query: LessonCancelQuery!
    $input: LessonCancellationInfo!
  ) {
    cancelLesson(query: $query, input: $input) {
      id
    }
  }
`

gql`
  mutation RemoveStudentFromLesson(
    $query: LessonRemoveStudentQuery!
    $input: LessonCancellationInfo!
  ) {
    removeStudentFromLesson(query: $query, input: $input) {
      id
    }
  }
`

gql`
  mutation RefundPurchase($input: RefundPurchaseInput!) {
    refundPurchase(input: $input) {
      id
      amountToRefund
    }
  }
`

gql`
  mutation EditPurchase($input: EditPurchaseInput!) {
    editPurchase(input: $input) {
      id
    }
  }
`

gql`
  mutation VoidPurchase($input: VoidPurchaseInput!) {
    purchaseLog: voidPurchase(input: $input) {
      id
    }
  }
`

gql`
  mutation createPurchase($input: CreatePurchaseInput!) {
    createPurchase(input: $input) {
      id
    }
  }
`

gql`
  mutation instructorUpdateLesson(
    $query: LessonUpdateQuery!
    $update: InstructorLessonUpdateInput!
  ) {
    instructorUpdateLesson(query: $query, update: $update) {
      id
    }
  }
`

gql`
  fragment CourseOrderShippingFragment on CourseOrder {
    courseId
    shipping {
      isComplete
      shippingMethod
      shippingDetails {
        label
        amount
      }
    }
  }

  mutation UpdateCourseOrderShipping(
    $query: CourseOrderQuery!
    $input: CourseOrderInput!
  ) {
    updateCourseOrder(query: $query, input: $input) {
      ...CourseOrderShippingFragment
    }
  }
`

function getCategory(categoryId: GQLPaymentCategories): GQLPaymentCategories {
  switch (categoryId.toString()) {
    case "comp":
      return GQLPaymentCategories.Complimentary
    case "na":
      return GQLPaymentCategories.NotApplicable
    default:
      return categoryId
  }
}

export type UpdatePackageSelectSkippedAction = ReturnType<
  typeof updatePackageSelectSkipped
>
export const updatePackageSelectSkipped = (isSkipped: boolean) => ({
  type: UPDATE_PACKAGE_SELECT_SKIPPED,
  payload: isSkipped,
})

export type UpdatePackageChoiceAction = ReturnType<typeof updatePackageChoice>
export const updatePackageChoice = (
  packageChoice: ProductPackageWithPrices,
) => ({
  type: UPDATE_PACKAGE_CHOICE,
  payload: packageChoice,
})

export type UpdateStudentAction = ReturnType<typeof updateStudent>
export const updateStudent = (student: User) => ({
  type: UPDATE_STUDENT,
  payload: student,
})

export type UpdateAdminStudentAction = ReturnType<typeof updateAdminStudent>
export const updateAdminStudent = (student: User | null) => ({
  type: UPDATE_ADMIN_STUDENT,
  payload: student,
})

export type ResetPackageChoiceAction = ReturnType<typeof resetPackageChoice>
export const resetPackageChoice = () => ({
  type: RESET_PACKAGE_CHOICE,
})

export type ResetBookingStateAction = ReturnType<typeof resetBookingState>
export const resetBookingState = () => ({
  type: RESET_BOOKING_STATE,
})

export type ResetAvailsAction = ReturnType<typeof resetAvails>
export const resetAvails = () => ({
  type: RESET_AVAILS,
})

type StudentDetailsWithoutMinutes = Omit<
  LessonStudentDetails,
  "drivingMinutes" | "observationMinutes"
>
export const saveLessonDetails = async (
  studentId: string,
  transientSlotId: string,
  studentDetails: StudentDetailsWithoutMinutes,
) => {
  await clientNext.mutate({
    mutation: ScheduleLessonDocument,
    variables: {
      input: {
        studentId,
        transientSlotId,
        studentDetails,
      },
    },
  })
}

export const selfBookSharedLesson = async (
  studentId: string,
  transientSlotId: string,
) => {
  await clientNext.mutate({
    mutation: SelfBookSharedLessonDocument,
    variables: {
      input: {
        addStudent: {
          studentId,
          isObserverAllowed: true,
          productQuantityList: [
            {
              productId: DRIVING_MINUTE_PRODUCT_ID,
              quantity: 60,
            },
            {
              productId: OBSERVATION_MINUTE_PRODUCT_ID,
              quantity: 60,
            },
          ],
        },
        transientSlotId,
      },
    },
  })
}

export type SetPriceBreakdownAction = ReturnType<typeof setPriceBreakdown>
export const setPriceBreakdown = (
  packageChoice: ProductPackageWithPrices,
  drivingMinutesRemaining: number,
) => {
  /**
   * After a user selects a package, we have to calculate the price they
   * will pay. This is based on the package they selected, any promos
   * that are running, and the amount of credit they have in their account.
   *
   * If they don't have any credit, they pay the full price - any promos.
   *
   * If they have enough credit, they don't owe anything, and can just book.
   *
   * Where it gets a bit more tricky is if they have some credit and are
   * buying a package. In this case (when they're buying a new package),
   * we need to let them buy that and _not_ use their credit. The reason
   * for this is if they want to buy, say, two packages. If we tried to
   * use credit for the second purchase, they would end up in an endless
   * loop where they just keep buying the same package with the same
   * credit. So if they're trying to buy anything other than the
   * single lesson "package", we don't apply their credit to the purchase.
   */
  if (!packageChoice) {
    throw new Error("Missing package choice in setPriceBreakdown")
  }

  let payload: PriceBreakdown

  const isSingleLessonPackage = packageChoice.lessonPackage
    ? packageChoice.lessonPackage.numLessons +
        packageChoice.lessonPackage.numRoadTests ===
      1
    : false

  if (!isSingleLessonPackage || !drivingMinutesRemaining) {
    // If they're buying a multi-lesson package or don't have credit, ignore credit.
    payload = {
      chargeAmount: packageChoice.price,
      drivingMinutesUsed: 0,
    }
  } else {
    // They're buying a single lesson package and have credit.
    if (drivingMinutesRemaining < 0) {
      throw new Error("User has negative account credits")
    }
    const creditInDollars = packageChoice.lessonPackage
      ? Math.round(
          (packageChoice.msrp / packageChoice.lessonPackage.drivingMinutes) *
            drivingMinutesRemaining,
        )
      : 0
    const priceAfterCredit = packageChoice.price - creditInDollars
    const drivingMinutesUsed = Math.min(
      packageChoice.lessonPackage?.totalMinutes ?? 0,
      drivingMinutesRemaining,
    )

    // Get the amount to charge. Make sure it doesn't go below 0.
    const chargeAmount = Math.max(priceAfterCredit, 0)

    payload = {
      chargeAmount,
      drivingMinutesUsed,
    }
  }

  return {
    type: SET_PRICE_BREAKDOWN,
    payload,
  }
}

interface AdminCreatePurchaseParams {
  address: string
  userId: string
  amountDollars: number
  state: string
  description: string
  chargeId: string
  customerId: string
  products: Product[]
  contractId: string | undefined
  categoryId: GQLPaymentCategories
}

export const adminCreatePurchase =
  ({
    address,
    userId,
    products,
    description,
    amountDollars,
    state,
    chargeId,
    customerId,
    categoryId,
    contractId,
  }: AdminCreatePurchaseParams): AppThunkAction<Promise<void>> =>
  async () => {
    const mutation = await clientNext.mutate({
      mutation: CreatePurchaseDocument,
      variables: {
        input: {
          ...(contractId && { contractId }),
          studentAddress: address,
          studentId: userId,
          amountDollars,
          description,
          stripeChargeId: chargeId,
          stripeCustomerId: customerId,
          categoryId: getCategory(categoryId),
          usStateId: state.toUpperCase() as GQLUsStateId,
          items: products.map((next) => ({
            itemId: next.itemId,
            productId: next.id,
            quantity: next.quantity,
            ...(next.customPrice && { customPrice: next.customPrice }),
          })),
          note: "Payment made in admin",
        },
      },
    })

    if (mutation.errors?.length) {
      throw new Error(mutation.errors[0].message)
    }
  }

interface CreateShippingFeePurchaseParams {
  address: string
  userId: string
  courseId: string
  shippingProductId: string
  shippingAmount: number
  shippingMethod: string
  shippingLabel: string
  chargeId: string
  customerId: string
  state: string
  email: string
}

export const createShippingFeePurchase =
  ({
    address,
    userId,
    courseId,
    shippingProductId,
    shippingAmount,
    shippingMethod,
    shippingLabel,
    chargeId,
    customerId,
    state,
    email,
  }: CreateShippingFeePurchaseParams): AppThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const mutation = await clientNext.mutate({
      mutation: CreatePurchaseDocument,
      variables: {
        input: {
          studentAddress: address,
          studentId: userId,
          amountDollars: shippingAmount,
          description: `${shippingLabel} [Shipping Fee]`,
          stripeChargeId: chargeId,
          stripeCustomerId: customerId,
          categoryId: GQLPaymentCategories.Stripe,
          usStateId: state.toUpperCase() as GQLUsStateId,
          items: [
            {
              itemId: uuidv4(),
              productId: shippingProductId,
              quantity: 1,
            },
          ],
        },
      },
    })

    if (mutation.errors?.length) {
      throw new Error(mutation.errors[0].message)
    }

    await clientNext.mutate({
      mutation: UpdateCourseOrderShippingDocument,
      variables: {
        query: {
          studentId: userId,
          courseId,
        },
        input: {
          shipping: {
            isComplete: true,
          },
        },
      },
    })

    const {
      location: { searchGeocode },
    } = getState()
    const orderInfo: TrackingOrderInfo = {
      amountDollars: shippingAmount,
      deliveryDate: formatDate(new Date().valueOf(), "local", "date"),
      category: "Course Shipping",
    }
    const product = {
      id: `course-shipping-${shippingMethod}`,
      title: shippingLabel,
      hasCourse: false,
    }
    trackOrderComplete(
      userId,
      email,
      searchGeocode,
      orderInfo,
      product,
      chargeId,
      state,
    )
  }

export const voidPurchase =
  (userId: string, purchaseLogId: string): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: VoidPurchaseDocument,
      variables: {
        input: {
          studentId: userId,
          purchaseId: purchaseLogId,
        },
      },
    })
  }

export const voidRefund =
  (userId: string, purchaseLogId: string): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: VoidRefundDocument,
      variables: {
        input: {
          studentId: userId,
          refundId: purchaseLogId,
        },
      },
    })
  }

interface EditPurchaseParams extends AdminCreatePurchaseParams {
  purchaseId: string
  versionId: string
  address: string
  categoryId: GQLPaymentCategories
  productPrices: Map<
    string,
    {
      id: string
      price: number
    }
  >
  paymentCaptureDate?: number
}

export const editPurchase =
  ({
    purchaseId,
    versionId,
    address,
    userId,
    products,
    description,
    amountDollars,
    state,
    chargeId,
    customerId,
    categoryId,
    paymentCaptureDate,
    contractId,
  }: EditPurchaseParams): AppThunkAction<Promise<void>> =>
  async () => {
    const isStripePurchase = categoryId === GQLPaymentCategories.Stripe
    const maybeChargeId = isStripePurchase ? chargeId : undefined
    const maybeCustomerId = isStripePurchase ? customerId : undefined
    await clientNext.mutate({
      mutation: EditPurchaseDocument,
      variables: {
        input: {
          purchaseId,
          versionId,
          studentAddress: address,
          studentId: userId,
          amountDollars,
          description,
          stripeChargeId: maybeChargeId,
          stripeCustomerId: maybeCustomerId,
          categoryId: getCategory(categoryId),
          usStateId: state.toUpperCase() as GQLUsStateId,
          items: products.map((next) => ({
            itemId: next.itemId,
            productId: next.id,
            quantity: next.quantity,
            ...(next.customPrice && { customPrice: next.customPrice }),
          })),
          paymentCaptureDate,
          ...(contractId && { contractId }),
        },
      },
    })
  }

interface RefundPurchaseParams {
  purchaseLogId: string
  userId: string
  amountDollars: number
  state: string
  description: string
  chargeId: string
  customerId: string
  categoryId: GQLPaymentCategories
  itemsToRefund: GQLItemToRefundInput[]
}

export const refundPurchase =
  ({
    purchaseLogId: purchaseId,
    categoryId,
    userId,
    amountDollars,
    description,
    chargeId,
    customerId,
    itemsToRefund,
  }: RefundPurchaseParams): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: RefundPurchaseDocument,
      variables: {
        input: {
          studentId: userId,
          purchaseId,
          versionId: purchaseId,
          amountToRefund: Math.abs(amountDollars),
          itemsToRefund,
          description,
          categoryId,
          stripeCustomerId: customerId,
          stripeChargeId: chargeId,
        },
      },
    })
  }

interface TransferPurchaseParams {
  id: string
  products: Product[]
  state: string
  note: string
  sourceUserId: string
  destinationUserId: string
}

export const transferPurchaseLog =
  ({
    id,
    products: packages,
    note,
    sourceUserId,
    destinationUserId,
  }: TransferPurchaseParams): AppThunkAction<Promise<void>> =>
  async () => {
    const itemsToTransfer: GQLItemToTransferInput[] = []
    let amountToTransfer = 0
    for (const pack of packages) {
      amountToTransfer += (pack.customPrice ?? pack.price ?? 0) * pack.quantity

      if (pack.products === undefined) {
        if (pack.quantity <= 0) continue
        itemsToTransfer.push({
          itemId: pack.itemId,
          transferredQuantity: pack.quantity,
        })
      }

      if ("products" in pack && !!pack.products) {
        for (const item of pack.products) {
          if (item.quantity <= 0) continue
          itemsToTransfer.push({
            itemId: item.itemId,
            transferredQuantity: item.quantity,
          })
        }
      }
    }

    if (itemsToTransfer.length === 0) {
      throw new Error("Nothing to transfer")
    }

    await clientNext.mutate({
      mutation: TransferPurchaseDocument,
      variables: {
        input: {
          sourceStudentId: sourceUserId,
          destinationStudentId: destinationUserId,
          purchaseId: id,
          itemsToTransfer,
          amountToTransfer,
          description: note,
        },
      },
    })
  }

interface MoveLessonParams {
  destId: string
  sourceStudentId: string
  lessonId: string
  note: string
  migratePurchases: boolean
}

export const moveLesson =
  ({
    destId,
    sourceStudentId,
    lessonId,
    note,
    migratePurchases,
  }: MoveLessonParams): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: TransferLessonDocument,
      variables: {
        input: {
          sourceStudentId,
          destinationStudentId: destId,
          lessonId,
          description: note,
          shouldTransferPurchases: !!migratePurchases,
        },
      },
    })
  }

interface ExpireProductsParams {
  studentId: string
  purchaseId: string
  reason: string
  expirationType: string
  items: GQLItemToExpireInput[]
}
const LegacyToLedgerExpireType: { [key: string]: GQLExpireProductsTypes } = {
  "no-show": GQLExpireProductsTypes.NO_SHOW,
  unused: GQLExpireProductsTypes.EXPIRED,
  other: GQLExpireProductsTypes.OTHER,
}

export const expireProducts =
  ({
    studentId,
    purchaseId,
    reason,
    expirationType,
    items,
  }: ExpireProductsParams): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: ExpireProductsDocument,
      variables: {
        input: {
          purchaseId,
          studentId,
          description: reason,
          type: LegacyToLedgerExpireType[expirationType],
          itemsToExpire: items,
        },
      },
    })
  }

export const voidExpiration = (studentId: string, expirationId: string) =>
  clientNext.mutate({
    mutation: VoidExpirationDocument,
    variables: {
      input: {
        studentId,
        expirationId,
      },
    },
  })

export const transferExpiration = (
  sourceStudentId: string,
  expirationId: string,
  description: string,
  destinationStudentId: string,
) =>
  clientNext.mutate({
    mutation: TransferExpirationDocument,
    variables: {
      input: {
        expirationId,
        sourceStudentId,
        description,
        destinationStudentId,
      },
    },
  })

type ChangeLessonDetails = GQLLessonUpdateInput["changeLessonDetails"]
type ChangeLessonDetailsFull = Exclude<ChangeLessonDetails, null | undefined>
type ChangeLessonKey = keyof ChangeLessonDetailsFull
const getters: {
  get: (
    details: LessonDetails,
  ) => ChangeLessonDetailsFull[keyof ChangeLessonDetailsFull]
  key: ChangeLessonKey
}[] = [
  { key: "startTime", get: (details) => details.startTime },
  { key: "endTime", get: (details) => details.endTime },
  { key: "price", get: (details) => details.price },
  { key: "isShareable", get: (details) => details.isShareable },
  { key: "pickupLocation", get: (details) => details.pickupLocation },
  {
    key: "lessonType",
    get: (details) => ({
      name: details.lessonType.name,
      label: details.lessonType.label,
      durationMinutes: details.lessonType.durationMinutes,
      ...(details.lessonType.filterLabel && {
        filterLabel: details.lessonType.filterLabel,
      }),
    }),
  },
]

type Unarray<T> = T extends Array<infer Element> ? Element : never
type ChangeStudentDetails = GQLLessonUpdateInput["changeStudentDetails"]
type ChangeStudentDetailsElement = Unarray<
  Exclude<ChangeStudentDetails, null | undefined>
>

type AddStudentList = GQLLessonUpdateInput["addStudent"]

function setByKey<Obj, Key extends keyof Obj>(
  obj: Obj,
  key: Key,
  val: Obj[Key],
) {
  // eslint-disable-next-line no-param-reassign
  obj[key] = val
}

function convertAllowObserverToBool(
  allowObserver: string | undefined,
): boolean {
  if (!allowObserver) return false
  if (allowObserver.toLowerCase() !== "yes") return false
  return true
}

function convertMinutesToQuantityList(minutes: {
  drivingMinutes: number
  observationMinutes: number
}): GQLProductQuantity[] {
  return [
    {
      productId: OBSERVATION_MINUTE_PRODUCT_ID,
      quantity: minutes.observationMinutes,
    },
    {
      productId: DRIVING_MINUTE_PRODUCT_ID,
      quantity: minutes.drivingMinutes,
    },
  ]
}

export const updateLesson =
  (
    instructorId: string,
    lessonId: string,
    prevLessonObj: InstructorLesson,
    newLessonObj: InstructorLesson,
  ): AppThunkAction<Promise<void>> =>
  async () => {
    let changeLessonDetails: ChangeLessonDetails

    for (const { key, get } of getters) {
      if (get(prevLessonObj.details) !== get(newLessonObj.details)) {
        changeLessonDetails = changeLessonDetails ?? {}
        // not using assignment bc of typescript
        // `setVal` is a workaround
        setByKey(changeLessonDetails, key, get(newLessonObj.details))
      }
    }

    let changeStudentDetails: ChangeStudentDetails

    for (const [studentId, origDetails] of Object.entries(
      prevLessonObj.students,
    )) {
      const updDetails = newLessonObj.students[studentId]
      let changeStudentDetailsElement: ChangeStudentDetailsElement | undefined

      const origAllowObserver = convertAllowObserverToBool(
        origDetails.allowObserver,
      )
      const updAllowObserver = convertAllowObserverToBool(
        updDetails.allowObserver,
      )
      if (origAllowObserver !== updAllowObserver) {
        changeStudentDetailsElement = changeStudentDetailsElement ?? {
          studentId,
        }
        changeStudentDetailsElement.isObserverAllowed = updAllowObserver
      }

      if (origDetails.dmvLocation !== updDetails.dmvLocation) {
        changeStudentDetailsElement = changeStudentDetailsElement ?? {
          studentId,
        }
        changeStudentDetailsElement.dmvLocation = updDetails.dmvLocation
      }

      if (origDetails.dmvConfirmationCode !== updDetails.dmvConfirmationCode) {
        changeStudentDetailsElement = changeStudentDetailsElement ?? {
          studentId,
        }
        changeStudentDetailsElement.dmvConfirmationCode =
          updDetails.dmvConfirmationCode
      }

      if (origDetails.pickupDetails !== updDetails.pickupDetails) {
        changeStudentDetailsElement = changeStudentDetailsElement ?? {
          studentId,
        }
        changeStudentDetailsElement.pickupDetails =
          updDetails.pickupDetails ?? undefined
      }

      if (
        origDetails.drivingMinutes !== updDetails.drivingMinutes ||
        origDetails.observationMinutes !== updDetails.observationMinutes
      ) {
        changeStudentDetailsElement = changeStudentDetailsElement ?? {
          studentId,
        }
        changeStudentDetailsElement.productQuantityList =
          convertMinutesToQuantityList(updDetails)
      }

      if (changeStudentDetailsElement) {
        changeStudentDetails = changeStudentDetails ?? []
        changeStudentDetails.push(changeStudentDetailsElement)
      }
    }

    const prevStudentIds = Object.keys(prevLessonObj.students)
    const studentsToAdd = Object.keys(newLessonObj.students).filter(
      (studentId) => !prevStudentIds.includes(studentId),
    )

    const addStudentList: AddStudentList = studentsToAdd.map((studentId) => {
      const studentDetails = newLessonObj.students[studentId]
      return {
        studentId,
        isObserverAllowed: convertAllowObserverToBool(
          studentDetails.allowObserver,
        ),
        pickupDetails: studentDetails.pickupDetails ?? undefined,
        dmvLocation: studentDetails.dmvLocation ?? undefined,
        dmvConfirmationCode: studentDetails.dmvConfirmationCode ?? undefined,
        productQuantityList: convertMinutesToQuantityList(studentDetails),
      }
    })

    await clientNext.mutate({
      mutation: UpdateLessonDocument,
      variables: {
        query: {
          instructorId,
          id: lessonId,
        },
        update: {
          changeLessonDetails,
          changeStudentDetails,
          addStudent: addStudentList,
        },
      },
    })
  }

export const cancelLesson =
  (
    lesson: InstructorLesson,
    reason: string,
    initiatedBy: string,
    cancelPolicy: CancelPolicy,
  ): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: CancelLessonDocument,
      variables: {
        query: {
          instructorId: lesson.instructor.id,
          id: lesson.id,
        },
        input: {
          initiatedBy: toGQLInitiatedBy[initiatedBy],
          description: reason,
          cancelPolicy,
        },
      },
    })
  }

export const removeStudentFromLesson =
  (
    lesson: UserLesson,
    studentIdToRemove: string,
    reason: string,
    initiatedBy: string,
    cancelPolicy: CancelPolicy,
  ): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: RemoveStudentFromLessonDocument,
      variables: {
        query: {
          instructorId: lesson.instructor.id,
          studentId: studentIdToRemove,
          id: lesson.id,
        },
        input: {
          initiatedBy: toGQLInitiatedBy[initiatedBy],
          description: reason,
          cancelPolicy,
        },
      },
    })
  }

export const instructorUpdateLesson =
  ({
    query,
    update: updateArg,
  }: {
    query: GQLLessonUpdateQuery
    update: GQLInstructorLessonUpdateInput
  }): AppThunkAction<Promise<void>> =>
  async () => {
    await clientNext.mutate({
      mutation: InstructorUpdateLessonDocument,
      variables: {
        query,
        update: updateArg,
      },
    })
  }
